import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataTable, Badge } from "@/components/common/DataTable";
import { PlaceholderPanel } from "@/components/common/PlaceholderPanel";
import { customerService, contactService, addressService, segmentService, auditService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import LoyaltyTab from "@/features/loyalty/LoyaltyTab";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Star, Trash2, Loader2, Sparkles, Clock, X, MapPin,
  MessageSquare, ShoppingCart, CreditCard, LifeBuoy, HeartPulse, Cpu, FileText,
} from "lucide-react";

const STATUS_COLOR = { Active: "green", Inactive: "slate", Prospect: "amber", Blocked: "red" };
const TABS = [
  ["overview", "Overview"], ["contacts", "Contacts"], ["addresses", "Addresses"],
  ["preferences", "Preferences"], ["notes", "Notes"], ["segments", "Segments & Tags"],
  ["timeline", "Timeline"], ["history", "History"], ["intelligence", "Intelligence"],
  ["documents", "Documents"],
];

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, entitlements } = useAuth();
  const canEdit = hasPermission("CUSTOMER.EDIT");
  const loyaltyEnabled = (entitlements?.Module || []).includes("LOYALTY") && hasPermission("LOYALTY.VIEW");
  const tabs = loyaltyEnabled ? [...TABS.slice(0, 8), ["loyalty", "Loyalty"], ...TABS.slice(8)] : TABS;
  const [customer, setCustomer] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [allSegments, setAllSegments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const reloadCustomer = useCallback(async () => {
    const c = await customerService.get(id);
    setCustomer(c);
    return c;
  }, [id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, k, a] = await Promise.all([
        customerService.get(id), customerService.contacts(id), addressService.list(id),
      ]);
      setCustomer(c); setContacts(k.items || []); setAddresses(a.items || []);
      segmentService.list().then((s) => setAllSegments((s.items || []).filter((x) => x.tenantId === c.tenantId))).catch(() => {});
      if (hasPermission("AUDIT.VIEW")) {
        auditService.list({ entityType: "CUSTOMER", entityId: id, size: 50 })
          .then((au) => setAudit(au.items || [])).catch(() => {});
      }
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id, hasPermission]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!customer) return <div className="text-muted-foreground">Customer not found.</div>;

  return (
    <div data-testid="customer-detail">
      <button onClick={() => navigate("/admin/customers")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-customers">
        <ArrowLeft className="h-4 w-4" /> Back to Customers
      </button>

      <div className="bg-card border border-border rounded-xl p-6 shadow-sm mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{customer.customerName}</h1>
            <p className="text-sm text-muted-foreground mt-1 font-mono">{customer.customerCode}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge color={STATUS_COLOR[customer.status] || "slate"}>{customer.status}</Badge>
            <Badge color="slate">{customer.customerType}</Badge>
            <Badge color="blue">Health: {(customer.healthScore || {}).band || "Unknown"}</Badge>
            {(customer.tags || []).slice(0, 4).map((t) => <Badge key={t} color="teal">{t}</Badge>)}
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-5 border-b border-border overflow-x-auto">
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`detail-tab-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab customer={customer} addresses={addresses} contactsCount={contacts.length} />}
      {tab === "contacts" && <ContactsTab customer={customer} contacts={contacts} canEdit={canEdit} hasPermission={hasPermission} reload={load} navigate={navigate} />}
      {tab === "addresses" && <AddressesTab customer={customer} addresses={addresses} canEdit={canEdit} reload={load} />}
      {tab === "preferences" && <PreferencesTab customerId={id} canEdit={canEdit} />}
      {tab === "notes" && <NotesTab customerId={id} canEdit={canEdit} />}
      {tab === "segments" && <SegmentsTagsTab customer={customer} allSegments={allSegments} canEdit={canEdit} reload={reloadCustomer} setCustomer={setCustomer} />}
      {tab === "timeline" && <TimelineTab audit={audit} />}
      {tab === "history" && <HistoryTab />}
      {tab === "intelligence" && <IntelligenceTab customer={customer} />}
      {tab === "loyalty" && <LoyaltyTab customerId={id} canEdit={hasPermission("LOYALTY.UPDATE")} />}
      {tab === "documents" && (
        <div data-testid="detail-documents">
          <PlaceholderPanel icon={FileText} title="Customer Documents"
            description="Contracts, invoices and uploaded files for this customer will be linked here via the File Management module in a later phase." />
        </div>
      )}
    </div>
  );
}

/* ---------- Overview ---------- */
function OverviewTab({ customer, addresses, contactsCount }) {
  const primary = addresses.find((a) => a.isPrimary) || addresses[0];
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="detail-overview">
      <Section title="Profile">
        <Row label="Email" value={customer.email} />
        <Row label="Mobile" value={customer.mobile} />
        <Row label="Industry" value={customer.industry} />
        <Row label="Segment field" value={customer.segment} />
        <Row label="Lifecycle" value={customer.lifecycleStage} />
        <Row label="Contacts" value={String(contactsCount)} />
      </Section>
      <Section title="Primary Address">
        {primary ? (
          <>
            <Row label="Type" value={primary.type} />
            <Row label="City" value={[primary.city, primary.state].filter(Boolean).join(", ")} />
            <Row label="Country" value={primary.country} />
            <Row label="Postal" value={primary.postalCode} />
          </>
        ) : <p className="text-sm text-muted-foreground">No address on file.</p>}
      </Section>
    </div>
  );
}

/* ---------- Contacts ---------- */
function ContactsTab({ customer, contacts, canEdit, hasPermission, reload, navigate }) {
  const [showAdd, setShowAdd] = useState(false);
  const [cf, setCf] = useState({ firstName: "", lastName: "", title: "", email: "", mobile: "", isPrimary: false });
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!cf.firstName) return toast.error("First name is required");
    setSaving(true);
    try {
      const clean = Object.fromEntries(Object.entries(cf).filter(([, v]) => v !== ""));
      await contactService.create({ ...clean, customerId: customer.id, tenantId: customer.tenantId });
      toast.success("Contact added"); setShowAdd(false);
      setCf({ firstName: "", lastName: "", title: "", email: "", mobile: "", isPrimary: false }); reload();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const setPrimary = async (c) => { await contactService.setPrimary(c.id); reload(); };
  const remove = async (c) => { if (window.confirm(`Delete ${c.fullName}?`)) { await contactService.remove(c.id); reload(); } };

  const cols = [
    { key: "fullName", header: "Name", render: (r) => <button onClick={() => navigate(`/admin/contacts/${r.id}`)} className="font-medium text-foreground hover:text-primary flex items-center gap-1.5" data-testid={`open-contact-${r.id}`}>{r.fullName}{r.isPrimary && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}</button> },
    { key: "title", header: "Title", render: (r) => r.title || "—" },
    { key: "email", header: "Email", render: (r) => <span className="text-muted-foreground text-xs">{r.email || "—"}</span> },
    { key: "_a", header: "", render: (r) => (
      <div className="flex items-center justify-end gap-1">
        {hasPermission("CONTACT.EDIT") && !r.isPrimary && <button onClick={() => setPrimary(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" title="Set primary" data-testid={`detail-primary-${r.id}`}><Star className="h-4 w-4" /></button>}
        {hasPermission("CONTACT.DELETE") && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`detail-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
    ) },
  ];

  return (
    <div data-testid="detail-contacts">
      {hasPermission("CONTACT.CREATE") && (
        <div className="flex justify-end mb-3">
          <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-primary/90" data-testid="detail-add-contact"><Plus className="h-4 w-4" /> Add Contact</button>
        </div>
      )}
      <DataTable columns={cols} rows={contacts} loading={false} testid="detail-contacts-table" empty="No contacts linked" />
      {showAdd && (
        <Modal title="Add Contact" onClose={() => setShowAdd(false)} onSave={add} saving={saving} testid="detail-contact-dialog">
          <div className="grid grid-cols-2 gap-3">
            <Input label="First Name *" value={cf.firstName} onChange={(v) => setCf({ ...cf, firstName: v })} testid="detail-cf-first" />
            <Input label="Last Name" value={cf.lastName} onChange={(v) => setCf({ ...cf, lastName: v })} />
          </div>
          <Input label="Title" value={cf.title} onChange={(v) => setCf({ ...cf, title: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Email" value={cf.email} onChange={(v) => setCf({ ...cf, email: v })} />
            <Input label="Mobile" value={cf.mobile} onChange={(v) => setCf({ ...cf, mobile: v })} />
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cf.isPrimary} onChange={(e) => setCf({ ...cf, isPrimary: e.target.checked })} /> Set as primary</label>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Addresses ---------- */
function AddressesTab({ customer, addresses, canEdit, reload }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [af, setAf] = useState({ type: "Billing", label: "", line1: "", city: "", state: "", country: "", postalCode: "", isPrimary: false });
  const [saving, setSaving] = useState(false);

  const open = (a) => { setEditing(a || null); setAf(a ? { ...af, ...a } : { type: "Billing", label: "", line1: "", city: "", state: "", country: "", postalCode: "", isPrimary: false }); setShow(true); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing) await addressService.update(editing.id, { type: af.type, label: af.label, line1: af.line1, city: af.city, state: af.state, country: af.country, postalCode: af.postalCode });
      else await addressService.create({ ...af, customerId: customer.id });
      toast.success("Saved"); setShow(false); reload();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const setPrimary = async (a) => { await addressService.setPrimary(a.id); reload(); };
  const remove = async (a) => { if (window.confirm("Delete address?")) { await addressService.remove(a.id); reload(); } };

  return (
    <div data-testid="detail-addresses">
      {canEdit && <div className="flex justify-end mb-3"><button onClick={() => open(null)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-primary/90" data-testid="add-address-btn"><Plus className="h-4 w-4" /> Add Address</button></div>}
      <div className="grid md:grid-cols-2 gap-4">
        {addresses.length === 0 && <p className="text-sm text-muted-foreground">No addresses yet.</p>}
        {addresses.map((a) => (
          <div key={a.id} className="bg-card border border-border rounded-xl p-4 shadow-sm" data-testid={`address-${a.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-2 font-medium text-foreground"><MapPin className="h-4 w-4 text-primary" />{a.type}{a.isPrimary && <Badge color="green">Primary</Badge>}</span>
              {canEdit && (
                <div className="flex items-center gap-1">
                  {!a.isPrimary && <button onClick={() => setPrimary(a)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" title="Set primary" data-testid={`addr-primary-${a.id}`}><Star className="h-4 w-4" /></button>}
                  <button onClick={() => open(a)} className="text-xs text-primary hover:underline" data-testid={`addr-edit-${a.id}`}>Edit</button>
                  <button onClick={() => remove(a)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`addr-del-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{[a.line1, a.city, a.state, a.country, a.postalCode].filter(Boolean).join(", ") || "—"}</p>
          </div>
        ))}
      </div>
      {show && (
        <Modal title={editing ? "Edit Address" : "Add Address"} onClose={() => setShow(false)} onSave={save} saving={saving} testid="address-dialog">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium mb-1">Type</label><select value={af.type} onChange={(e) => setAf({ ...af, type: e.target.value })} className="ciq-input" data-testid="addr-type">{["Billing", "Shipping", "Office", "Home", "Other"].map((x) => <option key={x}>{x}</option>)}</select></div>
            <Input label="Label" value={af.label} onChange={(v) => setAf({ ...af, label: v })} />
          </div>
          <Input label="Address line" value={af.line1} onChange={(v) => setAf({ ...af, line1: v })} testid="addr-line1" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="City" value={af.city} onChange={(v) => setAf({ ...af, city: v })} testid="addr-city" />
            <Input label="State" value={af.state} onChange={(v) => setAf({ ...af, state: v })} testid="addr-state" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Country" value={af.country} onChange={(v) => setAf({ ...af, country: v })} testid="addr-country" />
            <Input label="Postal Code" value={af.postalCode} onChange={(v) => setAf({ ...af, postalCode: v })} testid="addr-postal" />
          </div>
          {!editing && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={af.isPrimary} onChange={(e) => setAf({ ...af, isPrimary: e.target.checked })} /> Primary address</label>}
        </Modal>
      )}
    </div>
  );
}

/* ---------- Preferences ---------- */
function PreferencesTab({ customerId, canEdit }) {
  const [pref, setPref] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { customerService.getPreferences(customerId).then(setPref).catch(() => {}); }, [customerId]);
  if (!pref) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  const consent = pref.consent || {};
  const setConsent = (k, v) => setPref({ ...pref, consent: { ...consent, [k]: v } });
  const save = async () => {
    setSaving(true);
    try {
      const { preferredChannel, preferredLanguage, timezone, doNotContact, marketingOptIn, frequencyCap } = pref;
      const saved = await customerService.savePreferences(customerId, { preferredChannel, preferredLanguage, timezone, doNotContact, marketingOptIn, frequencyCap, consent });
      setPref(saved); toast.success("Preferences saved");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm max-w-2xl" data-testid="detail-preferences">
      <div className="grid grid-cols-2 gap-4">
        <Input label="Preferred Channel" value={pref.preferredChannel || ""} onChange={(v) => setPref({ ...pref, preferredChannel: v })} testid="pref-channel" />
        <Input label="Language" value={pref.preferredLanguage || ""} onChange={(v) => setPref({ ...pref, preferredLanguage: v })} />
        <Input label="Timezone" value={pref.timezone || ""} onChange={(v) => setPref({ ...pref, timezone: v })} />
        <Input label="Frequency cap" value={pref.frequencyCap ?? ""} onChange={(v) => setPref({ ...pref, frequencyCap: v ? Number(v) : null })} />
      </div>
      <div className="flex gap-6 mt-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!pref.doNotContact} onChange={(e) => setPref({ ...pref, doNotContact: e.target.checked })} data-testid="pref-dnc" /> Do not contact</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!pref.marketingOptIn} onChange={(e) => setPref({ ...pref, marketingOptIn: e.target.checked })} /> Marketing opt-in</label>
      </div>
      <div className="mt-5">
        <p className="text-sm font-medium text-foreground mb-2">Channel consent</p>
        <div className="flex gap-5 flex-wrap">
          {["email", "sms", "whatsapp", "call"].map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm capitalize"><input type="checkbox" checked={!!consent[c]} onChange={(e) => setConsent(c, e.target.checked)} data-testid={`consent-${c}`} /> {c}</label>
          ))}
        </div>
      </div>
      {canEdit && <button onClick={save} disabled={saving} className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="pref-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Preferences</button>}
    </div>
  );
}

/* ---------- Notes ---------- */
function NotesTab({ customerId, canEdit }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => { customerService.notes(customerId).then((d) => setNotes(d.items || [])).catch(() => {}); }, [customerId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try { await customerService.addNote(customerId, { body }); setBody(""); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (n) => { await customerService.deleteNote(customerId, n.id); load(); };
  return (
    <div data-testid="detail-notes">
      {canEdit && (
        <div className="flex gap-2 mb-4">
          <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a note…" className="ciq-input flex-1" data-testid="note-input" />
          <button onClick={add} disabled={saving} className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="note-add">Add</button>
        </div>
      )}
      <div className="space-y-3">
        {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
        {notes.map((n) => (
          <div key={n.id} className="bg-card border border-border rounded-xl p-4 shadow-sm" data-testid={`note-${n.id}`}>
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm text-foreground">{n.body}</p>
              {canEdit && <button onClick={() => remove(n)} className="p-1 rounded hover:bg-destructive/10 text-destructive" data-testid={`note-del-${n.id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{n.authorName} · {(n.createdOn || "").slice(0, 16).replace("T", " ")}{n.pinned && " · pinned"}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Segments & Tags ---------- */
function SegmentsTagsTab({ customer, allSegments, canEdit, reload, setCustomer }) {
  const [tagInput, setTagInput] = useState((customer.tags || []).join(", "));
  const [pick, setPick] = useState("");
  const assigned = (customer.segmentIds || []).map((sid) => allSegments.find((s) => s.id === sid)).filter(Boolean);
  const available = allSegments.filter((s) => !(customer.segmentIds || []).includes(s.id));

  const saveTags = async () => {
    try { const d = await customerService.setTags(customer.id, tagInput.split(",")); setCustomer({ ...customer, tags: d.tags }); toast.success("Tags updated"); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };
  const assign = async () => { if (!pick) return; await customerService.assignSegment(customer.id, pick); setPick(""); await reload(); };
  const unassign = async (sid) => { await customerService.removeSegment(customer.id, sid); await reload(); };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="detail-segments">
      <Section title="Segments">
        <div className="flex flex-wrap gap-2 mb-3">
          {assigned.length === 0 && <p className="text-sm text-muted-foreground">No segments assigned.</p>}
          {assigned.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium text-white" style={{ background: s.color || "#0d9488" }} data-testid={`seg-chip-${s.id}`}>
              {s.name}{canEdit && <button onClick={() => unassign(s.id)} data-testid={`seg-remove-${s.id}`}><X className="h-3 w-3" /></button>}
            </span>
          ))}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <select value={pick} onChange={(e) => setPick(e.target.value)} className="ciq-input flex-1" data-testid="seg-select">
              <option value="">Add to segment…</option>
              {available.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button onClick={assign} className="bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="seg-assign">Assign</button>
          </div>
        )}
      </Section>
      <Section title="Tags">
        <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="comma, separated, tags" className="ciq-input mb-3" data-testid="tags-input" disabled={!canEdit} />
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(customer.tags || []).map((t) => <Badge key={t} color="teal">{t}</Badge>)}
        </div>
        {canEdit && <button onClick={saveTags} className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="tags-save">Save Tags</button>}
      </Section>
    </div>
  );
}

/* ---------- Timeline ---------- */
function TimelineTab({ audit }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="detail-timeline">
      {audit.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No activity recorded yet</p> : (
        <div className="space-y-3">
          {audit.map((a, i) => (
            <div key={i} className="flex items-start gap-3 text-sm border-b border-border pb-3">
              <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div><span className="font-medium text-foreground">{a.action}</span><span className="text-muted-foreground"> · {(a.timestamp || "").slice(0, 16).replace("T", " ")}</span></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- History (placeholders) ---------- */
function HistoryTab() {
  const items = [
    [MessageSquare, "Communication History", "Calls, emails and chat threads will appear here once Omnichannel is enabled."],
    [ShoppingCart, "Purchase History", "Orders and purchased products will surface here when Commerce/Orders is enabled."],
    [CreditCard, "Payment History", "Invoices and payments will appear here when Billing is enabled."],
    [LifeBuoy, "Support History", "Tickets and SLAs will appear here when the Ticketing module is enabled."],
  ];
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="detail-history">
      {items.map(([icon, title, desc]) => <PlaceholderPanel key={title} icon={icon} title={title} description={desc} />)}
    </div>
  );
}

/* ---------- Intelligence (placeholders) ---------- */
function IntelligenceTab({ customer }) {
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="detail-intelligence">
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm text-center">
        <HeartPulse className="h-8 w-8 text-primary mx-auto mb-3" />
        <h3 className="font-semibold text-foreground">Customer Health Score</h3>
        <p className="text-3xl font-bold text-foreground mt-3">{(customer.healthScore || {}).band || "Unknown"}</p>
        <p className="text-sm text-muted-foreground mt-2">Computed health scoring activates in a later AI phase.</p>
      </div>
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm text-center">
        <Cpu className="h-8 w-8 text-primary mx-auto mb-3" />
        <h3 className="font-semibold text-foreground">Customer Digital Twin</h3>
        <p className="text-sm text-muted-foreground mt-3">Status: {(customer.digitalTwin || {}).status || "not_available"}</p>
        <p className="text-sm text-muted-foreground mt-2">A behavioral digital twin will be modeled in a later AI phase.</p>
      </div>
    </div>
  );
}

/* ---------- shared bits ---------- */
function Section({ title, children }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
      <h3 className="font-semibold text-foreground mb-4">{title}</h3>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground text-right">{value || "—"}</span>
    </div>
  );
}
function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="ciq-input" data-testid={testid} />
    </div>
  );
}
function Modal({ title, children, onClose, onSave, saving, testid }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <h3 className="text-lg font-bold mb-4">{title}</h3>
        <div className="space-y-3.5">{children}</div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
          <button onClick={onSave} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="modal-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  );
}
